/* iceberg-ui */
import {
    Badge,
    Box,
    Button,
    Flex,
    IconWithVariant,
    Text
} from '@auviknetworks/iceberg-ui';

/* MUI */
import { useTheme } from '@mui/material/styles';

/* Shared */
import { Route } from 'application/Shared/Helpers';

/* Hooks */
import { useUserTheme } from 'hooks/dataAccess/User/userTheme';

/* Images */
import GoogleLogo from 'images/Google_G_Logo.svg';
import ADLogo from 'images/Microsoft-entra-id-logo.png';

/* React */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

const OptionCard = ({
    badge,
    examples,
    intro,
    minutes,
    routeToNextStep,
    title,
    type
}) => {
    const { t } = useTranslation();
    const theme = useTheme();

    return (
        <Flex
            data-testid="onboardingChooseOptionStepCard"
            flexDirection="column"
            p={4}
            backgroundColor={theme.palette.onboarding.backgroundColorSecondary}
            justifyContent="space-between"
            sx={{ position: 'relative' }}
            minHeight="500px"
            m={2}
        >
            {badge && (
                <Flex
                    width="100%"
                    marginLeft={-4} // Offset the m={2} above
                    position="absolute"
                    top="-10px"
                    justifyContent="center"
                >
                    <Badge
                        variant="primary"
                        text={t('Onboarding.chooseOptionStep.recommended')}
                    />
                </Flex>
            )}
            <Flex
                data-testid="onboardingChooseOptionStepSubCard"
                flexDirection="column"
            >
                <Text
                    fontSize={4}
                    color={theme.palette.onboarding.cardBodyText}
                    textAlign="center"
                >
                    {title}
                </Text>
                <Text
                    fontSize={1}
                    color={theme.palette.onboarding.cardBodyText}
                    mt={3}
                >
                    {intro}
                </Text>
                <Flex alignItems="center" justifyContent="center" my={3}>
                    <IconWithVariant
                        variant="primary"
                        src="Clock"
                        size={20}
                        wrapperProps={{
                            marginRight: '12px'
                        }}
                    />
                    <Text
                        fontSize={3}
                        color={theme.palette.onboarding.cardBodyText}
                        fontWeight={600}
                    >
                        {minutes === 1
                            ? t('Onboarding.time.minute')
                            : t('Onboarding.time.minute.plural', {
                                  minutes
                              })}
                    </Text>
                </Flex>
                {examples}
            </Flex>
            <Link
                to={routeToNextStep}
                style={{
                    alignSelf: 'center',
                    marginBottom: '16px',
                    marginTop: '16px'
                }}
            >
                <Button
                    data-testid={`onboardingChooseOptionStepBtn${type}`}
                    data-trackid={`onboardingChooseOptionStepBtn${type}`}
                    size="large"
                >
                    {t('Onboarding.select')}
                </Button>
            </Link>
        </Flex>
    );
};
const MiniCard = ({ img, imgHeight, name, type }) => {
    const theme = useTheme();
    return (
        <Flex
            mb={3}
            alignItems="center"
            justifyContent="center"
            p={2}
            width="190px"
            minHeight={5}
            backgroundColor={theme.palette.onboarding.backgroundColorPrimary}
        >
            {img && (
                <Box mr={3}>
                    <img
                        alt={`option_${type}`}
                        data-testid={`${type}_image`}
                        height="100%"
                        src={img}
                        style={{ maxHeight: imgHeight }}
                        width="100%"
                    />
                </Box>
            )}
            {name && <Box>{name}</Box>}
        </Flex>
    );
};

const quickScanItems = [
    {
        img: ADLogo,
        imgHeight: '16px',
        name: 'Onboarding.chooseOptionStep.runQuickScan.microsoftEntraID',
        type: 'microsoftEntraID'
    },
    {
        img: GoogleLogo,
        imgHeight: '16px',
        name: 'Onboarding.chooseOptionStep.runQuickScan.google',
        type: 'google'
    }
];

const deployAgentItems = (userTheme) => [
    {
        img: `/images/${userTheme}/integrations/cw_automate.png`,
        imgHeight: '48px',
        type: 'cwAutomate'
    },
    {
        img: `/images/${userTheme}/integrations/kaseya_vsa.png`,
        imgHeight: '48px',
        type: 'kaseya'
    },
    {
        type: 'more'
    }
];

const QuickScanExamples = () => {
    const { t } = useTranslation();
    return (
        <Flex
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
        >
            <MiniCard
                img={quickScanItems[0].img}
                imgHeight={quickScanItems[0].imgHeight}
                type={quickScanItems[0].type}
                key={quickScanItems[0].type}
                name={<Text>{t(quickScanItems[0].name)}</Text>}
            />
            <MiniCard
                img={quickScanItems[1].img}
                imgHeight={quickScanItems[1].imgHeight}
                type={quickScanItems[1].type}
                key={quickScanItems[1].type}
                name={<Text>{t(quickScanItems[1].name)}</Text>}
            />
        </Flex>
    );
};

const SaasAgentExamples = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const userTheme = useUserTheme();

    return (
        <Flex
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
        >
            <MiniCard
                img={deployAgentItems(userTheme)[0].img}
                imgHeight={deployAgentItems(userTheme)[0].imgHeight}
                type={deployAgentItems(userTheme)[0].type}
                key={deployAgentItems(userTheme)[0].type}
            />
            <MiniCard
                img={deployAgentItems(userTheme)[1].img}
                imgHeight={deployAgentItems(userTheme)[1].imgHeight}
                type={deployAgentItems(userTheme)[1].type}
                key={deployAgentItems(userTheme)[1].type}
            />
            <MiniCard
                type={deployAgentItems(userTheme)[2].type}
                name={
                    <Flex alignItems="center">
                        <Box mr={1}>
                            <Text
                                color={
                                    theme.palette.onboarding.tertiaryTextColor
                                }
                                fontSize={5}
                                fontWeight={600}
                            >
                                {t(
                                    'Onboarding.chooseOptionStep.deployAgent.andMore.number'
                                )}
                            </Text>
                        </Box>
                        <Box flex={1}>
                            {t(
                                'Onboarding.chooseOptionStep.deployAgent.andMore.text'
                            )}
                        </Box>
                    </Flex>
                }
            />
        </Flex>
    );
};

export const OptionCards = () => {
    const { t } = useTranslation();
    const { partnerId } = useParams();
    const basePartnerRoute = Route.buildBasePartnerRoute({ partnerId });
    return (
        <Flex
            alignItems="stretch"
            data-testid="onboardingChooseOptionStepCards"
            mt={4}
            flexDirection={['column', 'column', 'column', 'column', 'row']}
        >
            <OptionCard
                badge
                examples={<QuickScanExamples />}
                intro={t('Onboarding.chooseOptionStep.runQuickScan.intro')}
                minutes={2}
                nextStep="quickScanSelectStep"
                routeToNextStep={`${basePartnerRoute}/onboarding/quickScan/select`}
                title={t('Onboarding.chooseOptionStep.runQuickScan.title')}
                type="quickScan"
            />
            <OptionCard
                examples={<SaasAgentExamples />}
                intro={t('Onboarding.chooseOptionStep.deployAgent.intro')}
                minutes={6}
                nextStep="deployAgentSelectStep"
                routeToNextStep={`${basePartnerRoute}/onboarding/deployAgent/select`}
                title={t('Onboarding.chooseOptionStep.deployAgent.title')}
                type="deployAgent"
            />
        </Flex>
    );
};
